<template>
  <div>
    <div
      class="taskcard"
      :class="{
        'info-red-border': item.priority == boardItemPriority.High,
        'taskcard-error-border': itemHasError(item),
      }"
      @click="navigate(item)"
    >
      <div class="taskcard-header">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <i class="far fa-fw p-1 mr-2" :class="item.categoryIcon"></i>
            {{ item.categoryName }}
          </div>

          <div v-if="displayStatus || isSO" class="board-item-status-right mr-2">
            <div
              class="badge badge-pill"
              :class="{
                'badge-primary': item.boardItemStatus === boardItemStatus.Planned,
                'badge-info': item.boardItemStatus === boardItemStatus.Information,
                'badge-warning': item.boardItemStatus === boardItemStatus.InProgress,
                'badge-success': item.boardItemStatus == boardItemStatus.Done || item.boardItemStatus == boardItemStatus.UnderReview,
              }"
            >
              {{ boardItemStatusDisplayName }}
            </div>
          </div>

          <div v-if="item.listId !== boardItemListType.Done" class="d-flex align-items-center">
            <BoardItemPlannedDate :item="item" class="mr-2" />
            <DueBadge :item="item"></DueBadge>
          </div>

          <div v-if="item.isMoved && !item.isDelegated" class="badge badge-secondary badge-pill">
            <i class="fas fa-share"></i>
            <span class="ml-1">{{ initialBoardName }}</span>
          </div>
          <div v-if="item.isDelegated && item.ownerBoardId !== boardStore.currentBoardId" class="badge badge-secondary badge-pill">
            <i class="fas fa-repeat"></i>
            <span class="ml-1">{{ initialBoardName }}</span>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <div class="ml-1 font-weight-normal d-flex align-items-center">
            <i class="fa-fw far fa-angle-double-right p-1 mr-1"></i>
            {{ item.typeName }}
          </div>

          <div v-if="item.isRelevantForReport" class="badge badge-dark badge-pill p-1 mr-1 px-2">Report Relevant</div>
          <div v-if="item.isConfidential" class="badge badge-warning badge-pill p-1 mr-1 px-2">Confidential</div>
        </div>
      </div>

      <div class="taskcard-content">
        <div class="d-flex align-items-center justify-content-between">
          <div class="ml-2">
            <BoardItemLabels :item="item" :isSelectable="true" />

            <div class="title">{{ item.title }}</div>
            <div class="text-secondary description" v-if="!minimal">{{ item.description | stripHtml }}</div>
          </div>

          <div v-if="itemHasError(item)" class="taskcard-error-indicator">
            <i class="far fa-exclamation-triangle"></i>
          </div>

          <div class="align-self-start flex-1 d-flex align-items-center" v-if="!minimal">
            <div class="info-box mr-2 info-box-orange" v-if="showNotification(item)">
              <div class="info-item">
                <i class="fa fa-info fa-i" aria-hidden="true"></i>
              </div>
            </div>
          </div>

          <div
            class="align-self-start flex-1 d-flex align-items-center cursor-pointer"
            v-if="item.assignedToUserShortName && !minimal"
            @click.stop="selectUser($event)"
          >
            <div class="avatar">{{ item.assignedToUserShortName.substr(0, 3) }}</div>
          </div>
        </div>

        <div class="d-flex justify-content-between mt-1" v-if="!minimal">
          <div class="pl-2 d-flex">
            <div class="text-secondary" v-if="item.commentCount > 0">
              <i class="far fa-comment"></i>
              {{ item.commentCount }}
            </div>
          </div>

          <div>
            <div class="text-secondary" v-if="displayChecklistProgress">
              <i class="fas fa-tasks"></i>
              {{ closedChecklistsCount }}
            </div>
          </div>

          <div>
            <div class="text-secondary" v-if="item.attachmentCount > 0">
              <i class="far fa-file"></i>
              {{ item.attachmentCount }}
            </div>
          </div>
        </div>

        <div v-if="item.specialOccurrenceId && !isSO">
          <hr class="mb-1" />
          <div class="d-flex justify-content-center">
            <div class="badge badge-pill" :class="getOccurrenceClass(item)">
              {{ item.specialOccurrenceName || 'Occurrence' }}
            </div>
          </div>
        </div>

        <div
          class="d-flex justify-content-between validtimes flex-col"
          v-if="item.listName === listGroupType.Information"
        >
          <div class="flex-start" v-if="item.validFromDate">From: {{ item.validFromDate | formatDateTime }}</div>
          <div class="ml-auto" v-if="item.validToDate">To: {{ item.validToDate | formatDateTime }}</div>
        </div>

        <template v-if="sharedBoards.length">
          <hr class="mb-1" />
          <div class="mt-2">
            <div
              class="badge badge-pill p-1 mr-1 mb-1 px-2"
              :class="getSharedBadgeColor(board)"
              :title="getSharedBadgeTooltip(board)"
              v-b-tooltip:hover
              v-for="board in sharedBoards"
              :key="board.boardId"
            >
              {{ board.title }}
            </div>
          </div>
        </template>

        <BoardItemAcceptReject :item="item" :list="list" :view="acceptRejectView.Horizontal"></BoardItemAcceptReject>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import TaskDetail from './TaskDetail/TaskDetail.vue';
  import BoardItemAcceptReject from '@/components/BoardItem/BoardItemAcceptReject.vue';
  import BoardItemListType from '@/types/boardItemListType';

  import {
    IBoardItemCardDto,
    ListGroupTypeDto,
    BoardItemPriorityDto,
    BoardItemStatusDto,
    IBoardItemListDto,
    TechnicalOccurrenceViewDto,
    SharedBoardDto,
  } from '@/apiclient/apiclient_generated';
  import DueBadge from '@/components/DueBadge.vue';
  import BoardItemPlannedDate from '@/components/BoardItem/BoardItemPlannedDate.vue';
  import { BoardStore, BoardModule } from '@/store/board';
  import { NotificationStore, NotificationModule } from '@/store/notification';
  import AcceptRejectView from '@/types/acceptRejectView';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import BoardItemLabels from '@/components/Labels/BoardItemLabels.vue';
  import RouteNames from '@/types/routeNames';
  import formatDateTime from '@/filters/formatDateTime';

  @Component({
    components: {
      TaskDetail,
      DueBadge,
      BoardItemAcceptReject,
      BoardItemLabels,
      BoardItemPlannedDate,
    },
  })
  export default class TaskCard extends Vue {
    @Prop()
    public item: IBoardItemCardDto;
    @Prop()
    public isSO: boolean;
    @Prop()
    public list: IBoardItemListDto;
    @Prop({ default: false }) minimal: boolean;
    private notificationStore: NotificationModule = NotificationStore;
    private boardStore: BoardModule = BoardStore;
    private occurrenceStore: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private boardItemListType = BoardItemListType;
    private boardItemStatus = BoardItemStatusDto;
    private listGroupType = ListGroupTypeDto;
    private boardItemPriority = BoardItemPriorityDto;
    private acceptRejectView = AcceptRejectView;

    mounted() {
      // recalculate times etc.
      setInterval(() => this.$forceUpdate(), 30000);
    }

    get sharedBoards(): SharedBoardDto[] {
      return this.item.sharedBoards.filter((_) => _.boardId != this.boardStore.currentBoardId);
    }

    getSharedBadgeColor(board: SharedBoardDto) {
      return this.item.boardItemViews.some((t) => t.boardId === board.boardId) ? 'badge-success' : 'badge-secondary';
    }

    getSharedBadgeTooltip(board: SharedBoardDto) {
      const boardItemViews = this.item.boardItemViews.filter((t) => t.boardId === board.boardId && t.boardItemId == this.item.id);

      if (!boardItemViews.length) {
        return 'Not yet opened';
      }

      const latestView = this.lodash.orderBy(boardItemViews, (t) => t.lastViewDate, 'desc')[0];

      return `Last accessed: ${formatDateTime(latestView.lastViewDate)}`;
    }

    navigate(item: IBoardItemCardDto) {
      let routeName = '';

      if (this.isSO) {
        routeName = RouteNames.specialOccurrence.task;
      } else {
        routeName = 'BoardItemDetail';
      }

      if (this.$route.name == RouteNames.biTemplates) {
        routeName = 'TemplateDetail';
      }

      if (this.$route.name == RouteNames.specialOccurrence.attachments) {
        routeName = RouteNames.specialOccurrence.attachmentitem;
      }

      this.$router.push({ name: routeName, params: { itemid: item.id } });
    }

    getOccurrenceClass(boardItem: IBoardItemCardDto): string {
      const occurrences = this.occurrenceStore.activeAndArchivedOccurrences;

      const occ =
        occurrences.activeSpecialOccurrences?.find((_) => _.id == boardItem.specialOccurrenceId) ||
        occurrences.archivedSpecialOccurrences?.find((_) => _.id == boardItem.specialOccurrenceId);

      if (!occ) {
        return 'badge-so';
      }

      if (occ.isCompleted) {
        return 'badge-so-completed';
      }

      if (occ.type === 'TechnicalOccurrence' && (occ as TechnicalOccurrenceViewDto).technicalOccurrenceTypeId) {
        const typeInfo = this.occurrenceStore.technicalOccurrenceTypes.find(
          (_) => _.id === (occ as TechnicalOccurrenceViewDto).technicalOccurrenceTypeId
        );

        return typeInfo?.cssClass ?? 'badge-so';
      }

      return 'badge-so';
    }

    get displayChecklistProgress() {
      return this.item.checklistTotalItems > 0;
    }

    get closedChecklistsCount() {
      return `${this.item.checklistCompletedItems} of ${this.item.checklistTotalItems}`;
    }

    get displayStatus() {
      return (
        this.item.isDelegated &&
        (this.item.ownerBoardId === this.boardStore.currentBoardId || this.item.boardItemStatus === BoardItemStatusDto.UnderReview)
      );
    }

    get initialBoardName() {
      return this.boardStore.allBoards.find((_) => _.id === this.item.initialOwnerBoardId).title;
    }

    get boardItemStatusDisplayName() {
      switch (this.item.boardItemStatus) {
        case BoardItemStatusDto.InProgress:
          return 'In Progress';
        case BoardItemStatusDto.UnderReview:
          return 'Under Review';
        default:
          return this.item.boardItemStatus;
      }
    }

    itemHasError(item: IBoardItemCardDto) {
      return item?.hasActionError ?? false;
    }

    private showNotification(item: IBoardItemCardDto) {
      return this.notificationStore.notifications.includes(item.id) && this.list.displayNotifications;
    }

    private selectUser() {
      // Emit user selection event with user ID and short name
      this.$root.$emit('boardItem::userSelected', {
        id: this.item.assignedToUserId,
        shortName: this.item.assignedToUserShortName
      });
    }
  }
</script>

<style scoped lang="scss">
  .taskcard-error-border {
    border: solid 2px red !important;
    box-shadow: 1px 1px 3px red !important;

    .taskcard-error-indicator {
      font-size: 2rem;
      width: 50px;
      color: red;
    }
  }

  .taskcard {
    font-size: 0.8rem;
    border: solid 1px #ccc;
    outline: none;

    box-shadow: 0 1px 0 rgba(9, 30, 66, 0.25);
    background: white;
    transition: all 0.25s ease-in-out;
    z-index: 1000;
    border-radius: 3px;

    &.danger {
      border-top: solid 2px red;
    }

    .taskcard-header {
      background-color: lighten(#eaeaea, 7%);
      padding: 3px;
      font-weight: bold;
      border-bottom: solid 1px darken(#eaeaea, 2%);

      .badge {
        font-size: 0.65rem;
      }
    }

    .taskcard-content {
      padding: 7px;

      .validtimes {
        margin-top: 10px;
        color: white;

        div {
          padding: 5px;
          background: #357ebd;
          border-radius: 4px;
        }
      }
    }

    .title {
      font-size: 0.8rem;
    }

    &:not(.nodrag) {
      cursor: pointer;
    }

    .description {
      font-size: 0.8rem;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      width: 200px;
    }
  }

  .info-red-border {
    //border: 1px solid #e74c3c;
    box-shadow: 0 1px 2px -1px rgba(9, 30, 66, 0.25), 0 0 0 2px rgba(231, 76, 60, 0.8);

    .taskcard-header {
      background: #e74c3c !important;
      color: white;
    }
  }

  .info-box {
    height: 27px;
    width: 27px;
    border-radius: 4px;
  }
  .info-box-red {
    background: red;
  }
  .info-box-orange {
    background: #ffc107;
  }
  .info-item {
    height: 20px;
    width: 20px;
    margin: 2px;
    border: solid 2px white;
    border-radius: 50%;
    margin-left: 0.25rem !important;
    margin-top: 0.25rem !important;
  }
  .fa-i {
    color: white;
    margin-left: 0.43rem !important;
  }

  .board-item-status-right {
    flex-grow: 1;
    text-align: right;
  }
</style>
